import { z } from 'zod';
import {
  ActorType,
  ActorTypeSchema,
  AIRecommendation,
  AIRecommendationSchema,
  CaseDocumentStatus,
  CaseDocumentStatusSchema,
  CasePriority,
  CasePrioritySchema,
  CaseStatus,
  CaseStatusSchema,
} from '../enums/case.enums';

// ============================================================================
// RE-EXPORTS FOR BACKWARDS COMPATIBILITY
// ============================================================================

export {
  ActorType,
  ActorTypeSchema,
  AIRecommendation,
  AIRecommendationSchema,
  CaseDocumentStatus,
  CaseDocumentStatusSchema,
  CasePriority,
  CasePrioritySchema,
  CaseStatus,
  CaseStatusSchema,
};

// ============================================================================
// CASE RATE SOURCE (Issue #1470 follow-up — Gap 9)
// ============================================================================

/**
 * Source of the rate currently displayed/persisted on a Case.
 *
 * - `MANUAL`                     — Closer typed it; pricing engine not consulted.
 * - `PRICING_ENGINE`             — Read-through from latest pricing-engine output.
 * - `PRICING_ENGINE_OVERRIDDEN`  — Snapshot from pricing engine + user override; UI shows both.
 *
 * @see Issue #1470 — pricing-engine integration on Case.
 */
export const CaseRateSource = {
  MANUAL: 'MANUAL',
  PRICING_ENGINE: 'PRICING_ENGINE',
  PRICING_ENGINE_OVERRIDDEN: 'PRICING_ENGINE_OVERRIDDEN',
} as const;

export type CaseRateSource = (typeof CaseRateSource)[keyof typeof CaseRateSource];

/**
 * Zod schema mirroring `CaseRateSource`. Single source of truth: the schema
 * is built from `Object.values(CaseRateSource)` so adding a new source only
 * requires one edit.
 */
export const caseRateSourceSchema = z.enum(Object.values(CaseRateSource) as [CaseRateSource, ...CaseRateSource[]]);

// ============================================================================
// CASE DOCUMENT DTOs
// ============================================================================

/**
 * Case Document Schema
 */
export const CaseDocumentSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  caseId: z.string().uuid(),
  consumerId: z.string().uuid(),
  name: z.string(),
  originalName: z.string(),
  mimeType: z.string(),
  size: z.number().int(),
  category: z.string(),
  gcsPath: z.string(),
  status: CaseDocumentStatusSchema,
  verifiedBy: z.string().uuid().nullable().optional(),
  verifiedAt: z.string().datetime().nullable().optional(),
  rejectionReason: z.string().nullable().optional(),
  uploadedAt: z.string().datetime(),
});
export type CaseDocument = z.infer<typeof CaseDocumentSchema>;

/**
 * Case Document Response (for API responses)
 */
export const CaseDocumentResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  category: z.string(),
  status: CaseDocumentStatusSchema,
  mimeType: z.string(),
  size: z.number().int(),
  uploadedAt: z.string().datetime(),
  verifiedAt: z.string().datetime().nullable().optional(),
});
export type CaseDocumentResponse = z.infer<typeof CaseDocumentResponseSchema>;

// ============================================================================
// TIMELINE EVENT DTOs
// ============================================================================

/**
 * Timeline Event Schema
 */
export const TimelineEventSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  caseId: z.string().uuid(),
  type: z.string(),
  description: z.string(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  actorType: ActorTypeSchema,
  actorId: z.string().uuid().nullable().optional(),
  actorName: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
});
export type TimelineEvent = z.infer<typeof TimelineEventSchema>;

/**
 * Timeline Event Response (for API responses)
 */
export const TimelineEventResponseSchema = z.object({
  id: z.string().uuid(),
  type: z.string(),
  description: z.string(),
  actorType: ActorTypeSchema,
  actorName: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
});
export type TimelineEventResponse = z.infer<typeof TimelineEventResponseSchema>;

// ============================================================================
// CASE DTOs
// ============================================================================

/**
 * Consumer Info (nested in Case)
 */
export const CaseConsumerInfoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string().email(),
});
export type CaseConsumerInfo = z.infer<typeof CaseConsumerInfoSchema>;

/**
 * Assignee Info (nested in Case)
 */
export const CaseAssigneeInfoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string().email().optional(),
});
export type CaseAssigneeInfo = z.infer<typeof CaseAssigneeInfoSchema>;

/**
 * Full Case Schema
 */
export const CaseSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  consumerId: z.string().uuid(),
  caseNumber: z.string(),
  title: z.string(),
  type: z.string(),
  status: CaseStatusSchema,
  priority: CasePrioritySchema,
  description: z.string().nullable().optional(),
  assigneeId: z.string().uuid().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  riskScore: z.number().nullable().optional(),
  aiRecommendation: AIRecommendationSchema.nullable().optional(),
  redFlags: z.array(z.string()),
  dueDate: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  closedAt: z.string().datetime().nullable().optional(),
  // Nested relations (optional, included when requested)
  consumer: CaseConsumerInfoSchema.optional(),
  assignee: CaseAssigneeInfoSchema.optional(),
  documents: z.array(CaseDocumentResponseSchema).optional(),
  timeline: z.array(TimelineEventResponseSchema).optional(),
});
export type Case = z.infer<typeof CaseSchema>;

/**
 * Create Case Schema (Business Portal)
 */
export const CreateCaseSchema = z.object({
  consumerId: z.string().uuid(),
  title: z.string().min(1).max(255),
  type: z.string().min(1).max(100),
  description: z.string().optional(),
  priority: CasePrioritySchema.optional().default(CasePriority.MEDIUM),
  metadata: z.record(z.string(), z.unknown()).optional(),
  dueDate: z.string().datetime().optional(),
});
export type CreateCase = z.infer<typeof CreateCaseSchema>;

/**
 * Update Case Schema
 */
export const UpdateCaseSchema = z
  .object({
    title: z.string().min(1).max(255).optional(),
    type: z.string().min(1).max(100).optional(),
    description: z.string().optional(),
    priority: CasePrioritySchema.optional(),
    status: CaseStatusSchema.optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    dueDate: z.string().datetime().nullable().optional(),
    consumerId: z.string().uuid().optional(),
    consumerEmail: z.string().email().optional(),
    consumerName: z.string().optional(),
  })
  .refine((data) => Object.keys(data).length > 0, {
    message: 'At least one field must be provided for update',
  });
export type UpdateCase = z.infer<typeof UpdateCaseSchema>;

/**
 * Assign Case Schema
 */
export const AssignCaseSchema = z.object({
  assigneeId: z.string().uuid(),
});
export type AssignCase = z.infer<typeof AssignCaseSchema>;

/**
 * Update Case Status Schema
 */
export const UpdateCaseStatusSchema = z.object({
  status: CaseStatusSchema,
  reason: z.string().optional(),
});
export type UpdateCaseStatus = z.infer<typeof UpdateCaseStatusSchema>;

// ============================================================================
// CONSUMER PORTAL CASE DTOs
// ============================================================================

/**
 * Consumer Create Case Schema
 * Used by consumers in self-service portal
 */
export const ConsumerCreateCaseSchema = z.object({
  title: z.string().min(1).max(255),
  type: z.string().min(1).max(100),
  description: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});
export type ConsumerCreateCase = z.infer<typeof ConsumerCreateCaseSchema>;

/**
 * Consumer Case Response
 * Limited view for consumers (excludes internal fields)
 */
export const ConsumerCaseResponseSchema = z.object({
  id: z.string().uuid(),
  caseNumber: z.string(),
  title: z.string(),
  type: z.string(),
  status: CaseStatusSchema,
  priority: CasePrioritySchema,
  description: z.string().nullable().optional(),
  dueDate: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  documents: z.array(CaseDocumentResponseSchema).optional(),
  timeline: z.array(TimelineEventResponseSchema).optional(),
});
export type ConsumerCaseResponse = z.infer<typeof ConsumerCaseResponseSchema>;

// ============================================================================
// CASE LIST DTOs
// ============================================================================

/**
 * Case List Filters Schema
 */
export const CaseListFiltersSchema = z.object({
  status: CaseStatusSchema.optional(),
  priority: CasePrioritySchema.optional(),
  consumerId: z.string().uuid().optional(),
  assigneeId: z.string().uuid().optional(),
  type: z.string().optional(),
  search: z.string().optional(),
});
export type CaseListFilters = z.infer<typeof CaseListFiltersSchema>;

/**
 * Case List Query Schema
 */
export const CaseListQuerySchema = CaseListFiltersSchema.extend({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
  sortBy: z.enum(['caseNumber', 'title', 'status', 'priority', 'createdAt', 'dueDate']).optional(),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export type CaseListQuery = z.infer<typeof CaseListQuerySchema>;

/**
 * Case List Response Schema
 */
export const CaseListResponseSchema = z.object({
  data: z.array(CaseSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});
export type CaseListResponse = z.infer<typeof CaseListResponseSchema>;

// ============================================================================
// REVIEW QUEUE DTOs
// ============================================================================

/**
 * Review Queue Item Schema
 * Cases pending review with AI recommendations
 */
export const ReviewQueueItemSchema = z.object({
  id: z.string().uuid(),
  caseNumber: z.string(),
  title: z.string(),
  type: z.string(),
  status: CaseStatusSchema,
  priority: CasePrioritySchema,
  riskScore: z.number().nullable(),
  aiRecommendation: AIRecommendationSchema.nullable(),
  redFlags: z.array(z.string()),
  consumer: CaseConsumerInfoSchema,
  assignee: CaseAssigneeInfoSchema.nullable(),
  dueDate: z.string().datetime().nullable().optional(),
  createdAt: z.string().datetime(),
});
export type ReviewQueueItem = z.infer<typeof ReviewQueueItemSchema>;

/**
 * Review Queue Response Schema
 */
export const ReviewQueueResponseSchema = z.object({
  data: z.array(ReviewQueueItemSchema),
  total: z.number(),
  stats: z.object({
    totalPending: z.number(),
    aiApproved: z.number(),
    aiRejected: z.number(),
    aiReview: z.number(),
    highPriority: z.number(),
    dueSoon: z.number(),
  }),
});
export type ReviewQueueResponse = z.infer<typeof ReviewQueueResponseSchema>;

/**
 * Review Queue Query Schema
 * Tab values aligned with tRPC router (Issue #127)
 */
export const ReviewQueueQuerySchema = z.object({
  tab: z.enum(['all', 'ai_approved', 'ai_rejected', 'ai_review', 'high_priority', 'due_today']).default('all'),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});
export type ReviewQueueQuery = z.infer<typeof ReviewQueueQuerySchema>;

// ============================================================================
// CASE DOCUMENT UPLOAD DTOs
// ============================================================================

/**
 * Document Upload Schema
 */
export const DocumentUploadSchema = z.object({
  category: z.string().min(1).max(100),
});
export type DocumentUpload = z.infer<typeof DocumentUploadSchema>;

/**
 * Document Verify Schema
 */
export const DocumentVerifySchema = z.object({
  status: z.enum(['verified', 'rejected']),
  rejectionReason: z.string().optional(),
});
export type DocumentVerify = z.infer<typeof DocumentVerifySchema>;

// ============================================================================
// ROLE-BASED RESPONSE TRANSFORMERS (Issue #65 Phase 4)
// ============================================================================

/**
 * Caller role for case response transformation
 */
export type CaseCallerRole = 'business' | 'consumer' | 'admin';

/**
 * Transform a full Case to ConsumerCaseResponse
 * Excludes internal/sensitive fields that consumers should not see
 *
 * @param caseData - Full case data
 * @returns Consumer-safe case response
 */
export function transformCaseForConsumer(caseData: Case): ConsumerCaseResponse {
  return {
    id: caseData.id,
    caseNumber: caseData.caseNumber,
    title: caseData.title,
    type: caseData.type,
    status: caseData.status,
    priority: caseData.priority,
    description: caseData.description,
    dueDate: caseData.dueDate,
    createdAt: caseData.createdAt,
    updatedAt: caseData.updatedAt,
    // Include documents and timeline if present, but filter internal details
    documents: caseData.documents,
    timeline: caseData.timeline?.map((event) => ({
      id: event.id,
      type: event.type,
      description: event.description,
      actorType: event.actorType,
      actorName: event.actorName,
      createdAt: event.createdAt,
    })),
  };
}

/**
 * Transform a full Case based on caller role
 * - business/admin: Returns full case data
 * - consumer: Returns limited view (no internal fields)
 *
 * @param caseData - Full case data
 * @param callerRole - Role of the API caller
 * @returns Appropriately filtered case response
 */
export function transformCaseByRole<T extends CaseCallerRole>(
  caseData: Case,
  callerRole: T,
): T extends 'consumer' ? ConsumerCaseResponse : Case {
  if (callerRole === 'consumer') {
    return transformCaseForConsumer(caseData) as T extends 'consumer' ? ConsumerCaseResponse : Case;
  }
  // Business and admin get full case data
  return caseData as T extends 'consumer' ? ConsumerCaseResponse : Case;
}

/**
 * Transform an array of Cases based on caller role
 *
 * @param cases - Array of full case data
 * @param callerRole - Role of the API caller
 * @returns Array of appropriately filtered case responses
 */
export function transformCasesByRole<T extends CaseCallerRole>(
  cases: Case[],
  callerRole: T,
): (T extends 'consumer' ? ConsumerCaseResponse : Case)[] {
  return cases.map((c) => transformCaseByRole(c, callerRole));
}

/**
 * Fields excluded from consumer view
 * Useful for documentation and validation
 */
export const CONSUMER_EXCLUDED_FIELDS = [
  'businessId',
  'consumerId',
  'assigneeId',
  'metadata',
  'riskScore',
  'aiRecommendation',
  'redFlags',
  'closedAt',
  'consumer',
  'assignee',
] as const;

/**
 * Type guard to check if a case response is a consumer response
 *
 * @param caseData - Case or ConsumerCaseResponse
 * @returns true if the response is consumer-filtered
 */
export function isConsumerCaseResponse(caseData: Case | ConsumerCaseResponse): caseData is ConsumerCaseResponse {
  // Consumer responses don't have these internal fields
  return !('riskScore' in caseData) && !('aiRecommendation' in caseData) && !('redFlags' in caseData);
}

// ============================================================================
// ORIGINATION CASE-FIELDS EXPANSION (Issue #1470)
// ============================================================================

/**
 * Vendor IDs attached to a case. JSON column on the Case row; this schema is
 * the single source of truth for write-side validation. Vendor entries are
 * point-in-time string IDs (typically borrower-of-record / vendor-registry
 * UUIDs) — promoting to a vendor-registry FK is a deferred non-goal per
 * epic #1460.
 */
export const caseVendorsSchema = z
  .object({
    closer: z.string().min(1).max(120).optional(),
    escrow: z.string().min(1).max(120).optional(),
    title: z.string().min(1).max(120).optional(),
    appraiser: z.string().min(1).max(120).optional(),
    attorney: z.string().min(1).max(120).optional(),
    /** Custom slot keyed by tenant-defined role labels. */
    custom: z.record(z.string().min(1).max(64), z.string().min(1).max(120)).optional(),
  })
  .strict();
export type CaseVendors = z.infer<typeof caseVendorsSchema>;

/**
 * Source channel where a case originated. Free-form on the column for
 * forward-compat with tenant-specific channels, but the pricing engine and
 * referral attribution expect values from this enum where possible.
 */
export const caseSourceChannelSchema = z.enum([
  'direct',
  'broker',
  'partner',
  'marketing',
  'repeat',
  'referral_program',
]);
export type CaseSourceChannel = z.infer<typeof caseSourceChannelSchema>;

/**
 * Patch shape for the four origination columns (Issue #1470). All fields
 * are optional — only present-keys are persisted.
 *
 * Cross-field invariant: a non-null `dutchAccrualRate` requires `isDutch=true`
 * (or the existing record to already be Dutch). Setting `isDutch=false` with
 * a non-null `dutchAccrualRate` in the same patch is rejected — Dutch loans
 * accrue interest on the FULL committed amount, so the rate is meaningless
 * outside that mode and persisting it would mislead pricing/AI readers.
 */
export const updateCaseOriginationFieldsSchema = z
  .object({
    sourceChannel: z.string().min(1).max(64).nullable().optional(),
    vendors: caseVendorsSchema.nullable().optional(),
    isDutch: z.boolean().optional(),
    /** Daily Dutch accrual rate. NULL when isDutch=false. */
    dutchAccrualRate: z.number().min(0).max(1).nullable().optional(),
  })
  .superRefine((data, ctx) => {
    const hasNonNullRate = data.dutchAccrualRate !== undefined && data.dutchAccrualRate !== null;

    // (1) isDutch=false with a non-null rate is contradictory.
    if (data.isDutch === false && hasNonNullRate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dutchAccrualRate'],
        message: 'dutchAccrualRate must be null or omitted when isDutch is false',
      });
    }

    // (2) Setting a rate without explicitly toggling isDutch=true in the same
    // patch is rejected — caller must commit the mode change to avoid silent
    // inconsistency. (The service layer also normalizes on the write side as
    // a defense-in-depth check; see CasesService.updateCase.)
    if (hasNonNullRate && data.isDutch !== true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['isDutch'],
        message: 'isDutch must be set to true when dutchAccrualRate is provided',
      });
    }
  });
export type UpdateCaseOriginationFieldsInput = z.infer<typeof updateCaseOriginationFieldsSchema>;
